import { BUILD_PROPERTIES } from '@/editor/constant/buildProperties';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { globalStores } from '@/editor/stores';
import { EEntityType, EOperateMode } from '@/editor/types';
import {
  EParamGroupLabel,
  EParamLabel,
  EParamType,
  EParamUnit,
  EParamsKeys,
  IEditParams,
  IEditParamsOnChange
} from '@/editor/types/editParamType';
import { getUniqueId } from '@/editor/utils';
import { getParamTitle, isDrawWallMode, isParamValueUnchanged } from '../utils';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';

function getArcWallEntityParamsInfo(wallId: string) {
  const arcWallData = globalStores.wallStore.getWallData(wallId);
  const { start, end, center, clockwise, height, thickness } = arcWallData?.baseData ?? {};
  if (start && end && center && clockwise !== undefined) {
    const arcSeg = new ArcSeg2d({ start, end, center, clockwise });
    const arcInfo = {
      sagitta: Math.round(Math.abs(arcSeg.getSagitta())),
      chord: Math.round(Point2dUtils.distanceTo(start, end)),
      arcLength: Math.round(arcSeg.length),
      radius: Math.round(arcSeg.radius)
    };

    const paramDatas: IEditParams = {
      id: wallId,
      type: EEntityType.Wall,
      title: getParamTitle(EOperateMode.DrawArcWall),
      group: [
        {
          id: EParamGroupLabel.SIZE,
          label: EParamGroupLabel.SIZE,
          fields: [
            EParamsKeys.thickness,
            EParamsKeys.sagitta,
            EParamsKeys.chord,
            EParamsKeys.length,
            EParamsKeys.radius,
            EParamsKeys.height
          ]
        }
      ],
      values: {
        [EParamsKeys.thickness]: thickness,
        [EParamsKeys.sagitta]: arcInfo.sagitta,
        [EParamsKeys.chord]: arcInfo.chord,
        [EParamsKeys.length]: arcInfo.arcLength,
        [EParamsKeys.radius]: arcInfo.radius,
        [EParamsKeys.height]: height
      },
      fields: {
        [EParamsKeys.thickness]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          label: EParamLabel.WALL_THICKNESS,
          options: [...BUILD_PROPERTIES.WALL.THICKNESS_OPTIONS],
          settings: {
            readonly: false,
            max: BUILD_PROPERTIES.MAX_INPUT,
            min: BUILD_PROPERTIES.MIN_INPUT
          }
        },
        [EParamsKeys.sagitta]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          label: EParamLabel.SAGITTA,
          settings: {
            readonly: false,
            max: 99999,
            min: 100
          }
        },
        [EParamsKeys.chord]: {
          type: EParamType.NUMBER,
          label: EParamLabel.CHORD,
          unit: EParamUnit.MM,
          settings: {
            readonly: true
          }
        },
        [EParamsKeys.length]: {
          label: EParamLabel.ARC_WALL_LENGTH,
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          settings: {
            readonly: true
          }
        },
        [EParamsKeys.radius]: {
          label: EParamLabel.RADIUS,
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          settings: {
            readonly: true
          }
        },
        [EParamsKeys.height]: {
          type: EParamType.NUMBER,
          label: EParamLabel.WALL_HEIGHT,
          unit: EParamUnit.MM,
          settings: {
            readonly: true
          }
        }
      }
    };

    return paramDatas;
  }

  return undefined;
}

export function getDrawWallParamInfo(operateMode: EOperateMode) {
  if (isDrawWallMode(operateMode)) {
    const paramDatas: IEditParams = {
      id: getUniqueId(), // 这里无意义
      type: operateMode,
      title: getParamTitle(operateMode),
      group: [
        {
          id: EParamGroupLabel.SIZE,
          label: EParamGroupLabel.SIZE,
          fields: [EParamsKeys.thickness, EParamsKeys.layerHeight]
        }
      ],
      values: {
        [EParamsKeys.thickness]: globalStores.paramStore.wallThickness,
        [EParamsKeys.layerHeight]: globalStores.paramStore.layerHeight
      },
      fields: {
        [EParamsKeys.thickness]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          label: EParamLabel.WALL_THICKNESS,
          options: [...BUILD_PROPERTIES.WALL.THICKNESS_OPTIONS],
          settings: {
            readonly: false,
            max: BUILD_PROPERTIES.MAX_INPUT,
            min: BUILD_PROPERTIES.MIN_INPUT
          }
        },
        [EParamsKeys.layerHeight]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          label: EParamLabel.LAYER_HEIGHT,
          settings: {
            readonly: true
          }
        }
      }
    };

    return paramDatas;
  }

  return undefined;
}

export function getWallEntityParamInfo(wallId: string) {
  const wallData = globalStores.wallStore.getWallData(wallId);
  if (!wallData) return undefined;
  if (wallData.baseData.center) {
    return getArcWallEntityParamsInfo(wallId);
  }
  const distance = Point2dUtils.distanceTo(wallData.baseData.start, wallData.baseData.end).toFixed(0);
  const thickness = wallData.baseData.thickness;
  const height = wallData.baseData.height;

  const paramDatas: IEditParams = {
    id: wallId,
    type: EEntityType.Wall,
    title: getParamTitle(EEntityType.Wall),
    group: [
      {
        id: EParamGroupLabel.SIZE,
        label: EParamGroupLabel.SIZE,
        fields: [EParamsKeys.thickness, EParamsKeys.height, EParamsKeys.length]
      }
    ],
    values: {
      [EParamsKeys.thickness]: thickness,
      [EParamsKeys.height]: height,
      [EParamsKeys.length]: Number(distance)
    },
    fields: {
      [EParamsKeys.thickness]: {
        type: EParamType.NUMBER,
        unit: EParamUnit.MM,
        label: EParamLabel.WALL_THICKNESS,
        options: [...BUILD_PROPERTIES.WALL.THICKNESS_OPTIONS],
        settings: {
          readonly: false,
          max: BUILD_PROPERTIES.MAX_INPUT,
          min: BUILD_PROPERTIES.MIN_INPUT
        }
      },
      [EParamsKeys.height]: {
        type: EParamType.NUMBER,
        label: EParamLabel.WALL_HEIGHT,
        unit: EParamUnit.MM,
        settings: {
          readonly: true
        }
      },
      [EParamsKeys.length]: {
        label: EParamLabel.WALL_LENGTH,
        type: EParamType.NUMBER,
        unit: EParamUnit.MM,
        settings: {
          readonly: true
        }
      }
    }
  };

  return paramDatas;
}

export function wallChangeHandler(params: Parameters<IEditParamsOnChange>[number]) {
  return new Promise<void>((resolve, reject) => {
    if (isParamValueUnchanged(params)) {
      resolve();
      return;
    }
    switch (params.key) {
      case EParamsKeys.thickness: {
        const value = Math.round(Number(params.value));
        void globalStores.wallOperation.modifyWallBaseData(params.id, { thickness: value }).then(resolve);
        break;
      }
      case EParamsKeys.sagitta: {
        const wallData = globalStores.wallStore.getWallData(params.id);
        if (wallData) {
          const newArcSeg = new ArcSeg2d({
            start: wallData.baseData.start,
            end: wallData.baseData.end,
            sagitta: wallData.baseData.clockwise
              ? Math.round(Number(params.value))
              : 0 - Math.round(Number(params.value))
          });
          void globalStores.wallOperation
            .modifyWallBaseData(params.id, { center: newArcSeg.center, clockwise: newArcSeg.clockwise })
            .then(resolve);
        } else {
          reject(new Error(`墙体不存在: ${params.id}`));
        }
        break;
      }
      default:
        reject(new Error(`不支持的墙体参数: ${params.key}`));
        break;
    }
  });
}
